import { getConfig } from '../config/env';
import {
  getInterestedListPath,
  shouldHideAddFamilies,
  shouldHideAppStore,
  shouldHideClaimsManagement,
  shouldHideFoodProgram,
  shouldHideForNetwork,
  shouldHideForNonDirectors,
  shouldHideForNonFCCs,
  shouldHideIfNotEnabledOnMarketplace,
  shouldHideOpenLedger,
  shouldHideTaxForms,
  shouldHideUnenrollV2,
} from './navigationUtils';
import { paths } from './paths';
import { FeatureFlagEnum, RouteNameEnum, RouteType } from './types';

import ImpersonateRedirect from '../Components/Admin/ImpersonateRedirect';
import { MigrationReport } from '../Components/Admin/MigrationReport';
import {
  AddFamilies,
  AddStudentPage,
  AddToBilling,
  Admin,
  AgingReports,
  AnnualTuitionIncreasesPage,
  AppStore,
  Attendance,
  BillingSettings,
  BillingSignUpPage,
  BusinessCoachingSurveyPage,
  CheckInCheckOutPage,
  ClaimCreate,
  ClaimUpdate,
  ClaimsList,
  Dashboard,
  DebitCardPage,
  DebugPage,
  Devices,
  Documents,
  EarnInterestPage,
  EnrolledFamilies,
  EnrollmentInvoicePage,
  EnrollmentsAdminPage,
  FinancialSummary,
  FoodProgram,
  InsurancePage,
  InvoiceEditConfirm,
  InvoiceEditDetails,
  InvoiceEditStudents,
  Invoices,
  KinderConnectPage,
  LearningBeyondPaper,
  Locations,
  MealInput,
  MealLoggingPage,
  MealPlanning,
  MealRecordCreate,
  MenuPlanning,
  MenuPlanningUpdate,
  ParentHandbook,
  ParentsListing,
  PayrollPage,
  Profile,
  RecurringPlanEditDetails,
  RecurringPlans,
  Rooms,
  Staff,
  StaffDetails,
  StudentAddPage,
  StudentEnrollment,
  StudentEnrollmentSummary,
  Students,
  StudentsImport,
  SubsidyPrepaymentPage,
  TaxForms1099Page,
  TaxesPage,
  TimeSpaceCalculator,
  Timesheet,
  Transactions,
  TriSharePage,
  TuitionPricingCoachingPage,
  Welcome,
  WhiteLabelWebsitePage,
  WonderschoolApp,
  WonderschoolPhoneNumberPage,
} from '../Pages';
import { OpenLedgerPage } from '../Pages/AppStore/OpenLedger/OpenLedgerPage';
import SubstituteTeachersPage from '../Pages/SubstituteTeachersPage';
import { UnenrollPage } from '../students/components/enrollment/UnenrollPage';
import { UnenrollmentsPage } from '../students/components/enrollment/UnenrollmentsPage';
import { StudentEditPage } from '../students/components/studentEditPage/StudentEditPage';

const routes: RouteType[] = [
  {
    name: RouteNameEnum.WELCOME,
    path: paths.welcome,
    component: Welcome,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.WELCOME),
  },
  // Add Families is a real CCMS page (writes Firestore via saveStudent /
  // saveContact), so it goes through RestrictedRoute like any other page.
  // Registered under both the canonical /ccms/add-families path and the
  // bare /add-families alias.
  {
    name: RouteNameEnum.ADD_FAMILIES,
    path: paths.addFamilies,
    component: AddFamilies,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.ADD_FAMILIES) || shouldHideAddFamilies(),
  },
  {
    name: RouteNameEnum.ADD_FAMILIES,
    path: paths.addFamiliesAlias,
    component: AddFamilies,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.ADD_FAMILIES) || shouldHideAddFamilies(),
  },
  {
    name: RouteNameEnum.ADD_TO_BILLING,
    path: paths.addToBilling,
    component: AddToBilling,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.ADD_TO_BILLING) || shouldHideAddFamilies(),
  },
  {
    name: RouteNameEnum.HOME,
    path: paths.home,
    component: Dashboard,
    featureFlag: FeatureFlagEnum.DASHBOARD,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.HOME),
  },
  {
    name: RouteNameEnum.MESSAGING,
    path: paths.external.messaging,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () =>
      shouldHideForNetwork(RouteNameEnum.MESSAGES) ||
      shouldHideIfNotEnabledOnMarketplace('messaging') ||
      shouldHideForNonDirectors(),
  },
  {
    name: RouteNameEnum.INTERESTED_MESSAGES,
    path: paths.external.messages,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.MESSAGES) || shouldHideForNonDirectors(),
  },
  {
    name: RouteNameEnum.DOCUMENTS,
    path: paths.documents,
    permission: 'can_list_forms',
    component: Documents,
    featureFlag: FeatureFlagEnum.DOCUMENTS,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.DOCUMENTS),
  },
  {
    name: RouteNameEnum.ENROLLED_FAMILIES,
    path: paths.enrolledFamilies,
    permission: 'can_list_students',
    component: EnrolledFamilies,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.ENROLLED_FAMILIES),
  },
  {
    name: RouteNameEnum.LOCATIONS,
    path: paths.locations,
    permission: 'can_edit_location', //This permission is not correct, but it's the only one that works
    component: Locations,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.LOCATIONS),
  },
  {
    name: RouteNameEnum.PARENT_HANDBOOK,
    path: paths.parentHandbook,
    component: ParentHandbook,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.PARENT_HANDBOOK),
  },
  {
    name: RouteNameEnum.PROFILE,
    path: paths.profile,
    component: Profile,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.PROFILE),
  },
  {
    name: RouteNameEnum.ROOMS,
    path: paths.rooms,
    permission: 'can_list_rooms',
    component: Rooms,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.ROOMS),
  },
  {
    name: RouteNameEnum.SHARED_DEVICES,
    path: paths.devices,
    permission: 'can_list_devices',
    component: Devices,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.SHARED_DEVICES),
  },
  {
    name: RouteNameEnum.DEBUG_PAGE,
    path: paths.debug,
    component: DebugPage,
  },
  {
    name: RouteNameEnum.STAFF,
    path: paths.staff,
    permission: 'can_list_staff',
    component: Staff,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.STAFF),
  },
  {
    name: RouteNameEnum.STAFF_DETAILS,
    path: paths.staffDetails,
    permission: 'can_list_staff',
    component: StaffDetails,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.STAFF_DETAILS),
  },
  {
    name: RouteNameEnum.STUDENT_ADD,
    path: paths.studentAdd,
    permission: 'can_create_student',
    component: StudentAddPage,
    featureFlag: FeatureFlagEnum.STUDENTS,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.STUDENT_ADD),
  },
  {
    name: RouteNameEnum.STUDENT_ADD_CHILD,
    path: paths.studentAddChild,
    permission: 'can_create_student',
    component: StudentAddPage,
    featureFlag: FeatureFlagEnum.STUDENTS,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.STUDENT_ADD_CHILD),
  },
  {
    name: RouteNameEnum.NEW_STUDENT_ADD,
    path: paths.newStudentAdd,
    permission: 'can_create_student',
    component: AddStudentPage,
    featureFlag: FeatureFlagEnum.STUDENTS,
  },
  {
    name: RouteNameEnum.NEW_STUDENT_ADD_CHILD,
    path: paths.newStudentAddChild,
    permission: 'can_create_student',
    component: AddStudentPage,
    featureFlag: FeatureFlagEnum.STUDENTS,
  },
  {
    name: RouteNameEnum.STUDENT_ENROLLMENT,
    path: paths.studentEnrollment,
    permission: 'can_edit_student',
    component: StudentEnrollment,
    featureFlag: FeatureFlagEnum.STUDENTS,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.STUDENT_ENROLLMENT),
  },
  {
    name: RouteNameEnum.STUDENT_ENROLLMENT_SUMMARY,
    path: paths.studentEnrollmentSummary,
    permission: 'can_edit_student',
    component: StudentEnrollmentSummary,
    featureFlag: FeatureFlagEnum.STUDENTS,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.STUDENT_ENROLLMENT_SUMMARY),
  },
  {
    name: RouteNameEnum.STUDENT_ENROLLMENT_INVOICE,
    path: paths.studentEnrollmentInvoice,
    permission: 'can_edit_student',
    component: EnrollmentInvoicePage,
    featureFlag: FeatureFlagEnum.STUDENTS,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.STUDENT_ENROLLMENT_INVOICE),
  },
  {
    name: RouteNameEnum.STUDENTS,
    path: paths.students,
    permission: 'can_list_students',
    component: Students,
    featureFlag: FeatureFlagEnum.STUDENTS,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.STUDENTS),
  },
  {
    name: RouteNameEnum.STUDENTS_FILTER,
    path: paths.studentsFilter,
    permission: 'can_list_students',
    component: Students,
    featureFlag: FeatureFlagEnum.STUDENTS,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.STUDENTS_FILTER),
  },
  {
    name: RouteNameEnum.STUDENT_DETAILS,
    path: paths.studentDetails,
    permission: 'can_list_students',
    component: StudentEditPage,
    featureFlag: FeatureFlagEnum.STUDENTS,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.STUDENT_DETAILS),
  },
  // Full-page "end this enrollment" flow. Replaces the modal that
  // used to open from the student detail page's "Unenroll Student"
  // button. Same component serves the form step and the post-submit
  // success step (family-script + copy-to-clipboard).
  {
    name: RouteNameEnum.STUDENT_UNENROLL,
    path: paths.studentUnenroll,
    permission: 'can_edit_student',
    component: UnenrollPage,
    featureFlag: FeatureFlagEnum.STUDENTS,
    shouldHide: () => shouldHideUnenrollV2() || shouldHideForNetwork(RouteNameEnum.STUDENT_UNENROLL),
  },
  // Dashboard of enrolled students with a scheduled last day. Gated by the
  // same is-unenroll-v2-enabled flag as the unenroll form so both surfaces
  // go live together.
  {
    name: RouteNameEnum.UNENROLLMENTS,
    path: paths.unenrollments,
    permission: 'can_list_students',
    component: UnenrollmentsPage,
    featureFlag: FeatureFlagEnum.STUDENTS,
    shouldHide: () => shouldHideUnenrollV2() || shouldHideForNetwork(RouteNameEnum.UNENROLLMENTS),
  },
  {
    name: RouteNameEnum.STUDENTS_IMPORT,
    path: paths.studentsImport,
    permission: 'can_edit_student',
    component: StudentsImport,
    featureFlag: FeatureFlagEnum.STUDENTS,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.STUDENTS_IMPORT),
  },
  {
    name: RouteNameEnum.CONTACTS,
    path: paths.parentsListing,
    permission: 'can_view_parents_details',
    component: ParentsListing,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.CONTACTS),
  },
  // Meal Planning routes
  {
    name: RouteNameEnum.FOOD_PROGRAM_MEALS,
    path: paths.foodProgramMeals,
    permission: 'can_edit_student', //TODO: change this permission
    component: MealPlanning,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.CALENDAR) || shouldHideFoodProgram(),
  },
  {
    name: RouteNameEnum.FOOD_PROGRAM_MEAL_CREATE,
    path: paths.foodProgramMealCreate,
    permission: 'can_edit_student', //TODO: change this permission
    component: MealInput,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.FOOD_PROGRAM_MEALS) || shouldHideFoodProgram(),
  },
  {
    name: RouteNameEnum.FOOD_PROGRAM_MEAL_UPDATE,
    path: paths.foodProgramMealUpdate,
    permission: 'can_edit_student', //TODO: change this permission
    component: MealInput,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.FOOD_PROGRAM_MEALS) || shouldHideFoodProgram(),
  },
  // Meal record routes
  {
    name: RouteNameEnum.FOOD_PROGRAM_MEAL_RECORD_CREATE,
    path: paths.foodProgramMealRecordCreate,
    permission: 'can_edit_student', //TODO: change this permission
    component: MealRecordCreate,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.FOOD_PROGRAM_MEAL_RECORD_CREATE) || shouldHideFoodProgram(),
  },
  // Menu planning routes
  {
    name: RouteNameEnum.FOOD_PROGRAM_MENU_PLANNING,
    path: paths.foodProgramMenuPlanning,
    permission: 'can_edit_student', //TODO: change this permission
    component: MenuPlanning,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.FOOD_PROGRAM_MENU_PLANNING) || shouldHideFoodProgram(),
  },
  {
    name: RouteNameEnum.FOOD_PROGRAM_MENU_PLANNING_EDIT,
    path: paths.foodProgramMenuPlanningUpdate,
    permission: 'can_edit_student', //TODO: change this permission
    component: MenuPlanningUpdate,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.FOOD_PROGRAM_MENU_PLANNING_EDIT) || shouldHideFoodProgram(),
  },
  // Claims management routes
  {
    name: RouteNameEnum.FOOD_PROGRAM_CLAIMS,
    path: paths.foodProgramClaims,
    permission: 'can_edit_student', //TODO: change this permission
    component: ClaimsList,
    shouldHide: () =>
      shouldHideForNetwork(RouteNameEnum.FOOD_PROGRAM_CLAIMS) ||
      shouldHideFoodProgram() ||
      shouldHideClaimsManagement(),
  },
  {
    name: RouteNameEnum.FOOD_PROGRAM_CLAIM_CREATE,
    path: paths.foodProgramClaimCreate,
    permission: 'can_edit_student', //TODO: change this permission
    component: ClaimCreate,
    shouldHide: () =>
      shouldHideForNetwork(RouteNameEnum.FOOD_PROGRAM_CLAIMS) ||
      shouldHideFoodProgram() ||
      shouldHideClaimsManagement(),
  },
  {
    name: RouteNameEnum.FOOD_PROGRAM_CLAIM_UPDATE,
    path: paths.foodProgramClaimUpdate,
    permission: 'can_edit_student', //TODO: change this permission
    component: ClaimUpdate,
    shouldHide: () =>
      shouldHideForNetwork(RouteNameEnum.FOOD_PROGRAM_CLAIMS) ||
      shouldHideFoodProgram() ||
      shouldHideClaimsManagement(),
  },
  // Reports routes
  {
    name: RouteNameEnum.AGING_REPORTS,
    path: paths.agingReports,
    permission: 'can_list_billing',
    component: AgingReports,
    featureFlag: FeatureFlagEnum.REPORTS,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.AGING_REPORTS),
  },
  {
    name: RouteNameEnum.ATTENDANCE,
    path: paths.attendance,
    permission: 'can_list_students',
    component: Attendance,
    featureFlag: FeatureFlagEnum.REPORTS,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.ATTENDANCE),
  },
  {
    name: RouteNameEnum.FINANCIAL_SUMMARY,
    path: paths.financialSummary,
    permission: 'can_list_billing',
    component: FinancialSummary,
    featureFlag: FeatureFlagEnum.REPORTS,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.FINANCIAL_SUMMARY),
  },
  {
    name: RouteNameEnum.STAFF_TIMESHEET,
    path: paths.staffTimesheet,
    permission: 'can_list_timecards',
    component: Timesheet,
    featureFlag: FeatureFlagEnum.REPORTS,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.STAFF_TIMESHEET),
  },
  {
    name: RouteNameEnum.FOOD_PROGRAM_REPORTS,
    path: paths.foodProgramReports,
    permission: 'can_list_transactions', // We should create a new permission for this
    component: FoodProgram,
    featureFlag: FeatureFlagEnum.REPORTS,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.FOOD_PROGRAM_REPORTS) || shouldHideFoodProgram(),
  },
  // Payments (billing) routes
  {
    name: RouteNameEnum.BILLING_SETTINGS,
    path: paths.billingSettings,
    permission: 'can_list_billing',
    component: BillingSettings,
    featureFlag: FeatureFlagEnum.FINANCES,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.BILLING_SETTINGS),
  },
  {
    name: RouteNameEnum.INVOICES,
    path: paths.invoices,
    permission: 'can_list_billing',
    component: Invoices,
    featureFlag: FeatureFlagEnum.FINANCES,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.INVOICES),
  },
  {
    name: RouteNameEnum.INVOICE_EDIT_CONFIRM,
    path: paths.invoiceEditConfirm,
    permission: 'can_edit_billing',
    component: InvoiceEditConfirm,
    featureFlag: FeatureFlagEnum.FINANCES,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.INVOICE_EDIT_CONFIRM),
  },
  {
    name: RouteNameEnum.INVOICE_EDIT_DETAILS,
    path: paths.invoiceEditDetails,
    permission: 'can_edit_billing',
    component: InvoiceEditDetails,
    featureFlag: FeatureFlagEnum.FINANCES,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.INVOICE_EDIT_DETAILS),
  },
  {
    name: RouteNameEnum.INVOICE_EDIT_STUDENTS,
    path: paths.invoiceEditStudents,
    permission: 'can_edit_billing',
    component: InvoiceEditStudents,
    featureFlag: FeatureFlagEnum.FINANCES,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.INVOICE_EDIT_STUDENTS),
  },
  {
    name: RouteNameEnum.RECURRING_PLANS,
    path: paths.recurringPlans,
    permission: 'can_list_billing',
    component: RecurringPlans,
    featureFlag: FeatureFlagEnum.FINANCES,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.RECURRING_PLANS),
  },
  {
    name: RouteNameEnum.RECURRING_PLAN_EDIT_DETAILS,
    path: paths.recurringPlanEditDetails,
    permission: 'can_edit_billing',
    component: RecurringPlanEditDetails,
    featureFlag: FeatureFlagEnum.FINANCES,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.RECURRING_PLAN_EDIT_DETAILS),
  },
  {
    name: RouteNameEnum.TIME_SPACE_CALCULATOR,
    path: paths.timeSpaceCalculator,
    permission: 'can_list_billing',
    featureFlag: FeatureFlagEnum.FINANCES,
    component: TimeSpaceCalculator,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.TIME_SPACE_CALCULATOR) || shouldHideForNonFCCs(),
  },
  {
    name: RouteNameEnum.TRANSACTIONS,
    path: paths.transactions,
    permission: 'can_list_billing',
    component: Transactions,
    featureFlag: FeatureFlagEnum.FINANCES,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.TRANSACTIONS),
  },

  // Marketplace routes
  {
    name: RouteNameEnum.EVENTS,
    path: paths.external.events,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.EVENTS),
  },
  {
    name: RouteNameEnum.HELP_CENTER,
    path: paths.external.helpCenter,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.HELP_CENTER,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.HELP_CENTER),
    shouldOpenInNewTab: true,
  },
  {
    name: RouteNameEnum.INTERESTED_FAMILIES,
    path: paths.external.interestedFamilies,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.INTERESTED_FAMILIES),
  },
  {
    name: RouteNameEnum.INTERESTED_LIST,
    // A getter so the destination follows the current program and impersonation
    // state, which can change after this module loads (MP-7001).
    get path() {
      return getInterestedListPath();
    },
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.INTERESTED_FAMILIES),
  },
  {
    name: RouteNameEnum.SHARED_LEADS,
    path: paths.external.sharedLeads,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.SHARED_LEADS) || shouldHideForNonDirectors(),
  },
  {
    name: RouteNameEnum.LOCATION_EDIT,
    path: paths.external.locationEdit,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.LOCATION_EDIT),
  },
  {
    name: RouteNameEnum.MARKETING,
    path: paths.external.marketing,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.MARKETING),
  },
  {
    name: RouteNameEnum.OPEN_HOUSE_EVENTS,
    path: paths.external.events,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.EVENTS),
  },
  {
    name: RouteNameEnum.SEARCH_SOCIAL_MARKETING,
    path: paths.external.marketing,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.MARKETING),
  },
  {
    name: RouteNameEnum.FLYERS,
    path: paths.external.flyers,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.FLYERS),
  },
  {
    name: RouteNameEnum.AI_PHONE,
    path: paths.external.aiPhone,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.AI_PHONE),
  },
  {
    name: RouteNameEnum.GROUP_MESSAGE,
    path: paths.external.groupMessage,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.GROUP_MESSAGE),
  },
  {
    name: RouteNameEnum.MARKETING_ANALYTICS,
    path: paths.external.marketingAnalytics,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.MARKETING_ANALYTICS),
  },
  {
    name: RouteNameEnum.MESSAGES,
    path: paths.external.messages,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.MESSAGES) || shouldHideForNonDirectors(),
  },
  {
    name: RouteNameEnum.ONBOARDING,
    path: paths.external.onboarding,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.ONBOARDING) || shouldHideForNonDirectors(),
  },
  {
    name: RouteNameEnum.TOURS,
    path: paths.external.tours,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.TOURS),
  },
  {
    name: RouteNameEnum.AVAILABILITY_SURVEY,
    path: paths.external.availabilitySurvey,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.AVAILABILITY_SURVEY),
  },
  {
    name: RouteNameEnum.TOUR_REQUESTS,
    path: paths.external.tourRequests,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () =>
      shouldHideForNetwork(RouteNameEnum.TOUR_REQUESTS) || shouldHideIfNotEnabledOnMarketplace('tourRequests'),
  },
  {
    name: RouteNameEnum.UPDATE_WEBSITE,
    path: paths.external.updateWebsite,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.UPDATE_WEBSITE) || shouldHideForNonDirectors(),
  },
  {
    name: RouteNameEnum.VIEW_WEBSITE,
    shouldOpenInNewTab: true,
    path: paths.external.viewWebsite,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.VIEW_WEBSITE),
  },
  {
    name: RouteNameEnum.SETTINGS,
    path: paths.external.settings,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.SETTINGS),
  },
  {
    name: RouteNameEnum.CREDENTIALS,
    path: paths.external.credentials,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.CREDENTIALS),
  },
  {
    name: RouteNameEnum.GOOGLE_MY_BUSINESS,
    path: paths.external.googleMyBusiness,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.GOOGLE_MY_BUSINESS),
  },
  {
    name: RouteNameEnum.YELP,
    path: paths.external.yelp,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.YELP),
  },

  // Admin routes
  {
    name: RouteNameEnum.IMPERSONATE,
    path: paths.impersonate,
    permission: 'can_list_moxit_organizations',
    component: ImpersonateRedirect,
  },
  {
    name: RouteNameEnum.AREA_51,
    path: paths.area51,
    permission: 'can_list_moxit_organizations',
    component: Admin,
  },
  {
    name: RouteNameEnum.ADMIN_DOCUMENTS,
    path: paths.adminDocuments,
    permission: 'can_list_moxit_organizations',
    component: Admin,
  },
  {
    name: RouteNameEnum.USERS,
    path: paths.users,
    permission: 'can_list_moxit_organizations',
    component: Admin,
  },
  {
    name: RouteNameEnum.NETWORKS,
    path: paths.networks,
    permission: 'can_list_moxit_organizations',
    component: Admin,
  },
  {
    name: RouteNameEnum.PROVIDERS,
    path: paths.providers,
    permission: 'can_list_moxit_organizations',
    component: Admin,
  },
  {
    name: RouteNameEnum.ENROLLMENTS_ADMIN,
    path: paths.enrollmentsAdmin,
    permission: 'can_list_moxit_organizations',
    component: EnrollmentsAdminPage,
  },
  {
    name: RouteNameEnum.MIGRATION_REPORT,
    path: paths.migrationReport,
    permission: 'can_list_moxit_organizations',
    component: MigrationReport,
  },
  {
    name: RouteNameEnum.TEACHER_RECRUITMENT_FULL_TIME,
    path: 'https://docs.google.com/forms/d/e/1FAIpQLScyFuX7ijvDodZUZMwZmaDxXbPRvX3IlnXAIC56R1dfDzhqVQ/viewform',
    permission: 'can_see_app_store',
    shouldOpenInNewTab: true,
  },
  {
    name: RouteNameEnum.TEACHER_RECRUITMENT_SUBSTITUTE,
    path: paths.substituteTeachers,
    permission: 'can_see_app_store',
    component: SubstituteTeachersPage,
  },
  {
    name: RouteNameEnum.WONDERSCHOOL_PHONE_NUMBER,
    path: paths.wonderschoolPhoneNumber,
    permission: 'can_see_app_store',
    component: WonderschoolPhoneNumberPage,
    isNew: true,
  },
  {
    name: RouteNameEnum.BUSINESS_COACHING_SURVEY,
    path: paths.businessCoachingSurvey,
    permission: 'can_see_app_store',
    component: BusinessCoachingSurveyPage,
    isNew: true,
  },
  {
    name: RouteNameEnum.CHECK_IN_CHECK_OUT,
    path: paths.checkInCheckOut,
    permission: 'can_see_app_store',
    component: CheckInCheckOutPage,
    isNew: true,
  },
  {
    name: RouteNameEnum.MEAL_LOGGING,
    path: paths.mealLogging,
    permission: 'can_see_app_store',
    component: MealLoggingPage,
    isNew: true,
  },
  {
    name: RouteNameEnum.TUITION_SETTINGS,
    path: paths.external.tuitionSettings,
    permission: 'can_see_app_store',
    shouldOpenInNewTab: true,
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.TUITION_SETTINGS),
  },
  {
    name: RouteNameEnum.COMPARABLE_PROGRAMS,
    path: paths.external.comparablePrograms,
    permission: 'can_list_marketplace_links',
    shouldOpenInNewTab: true,
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.COMPARABLE_PROGRAMS),
  },
  {
    name: RouteNameEnum.TUITION_GENERATOR,
    path: paths.external.tuitionGenerator,
    permission: 'can_list_marketplace_links',
    shouldOpenInNewTab: true,
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.TUITION_GENERATOR),
  },
  {
    name: RouteNameEnum.BILLING_SIGN_UP,
    path: paths.billingSignUp,
    permission: 'can_see_app_store',
    component: BillingSignUpPage,
    isNew: true,
  },
  {
    name: RouteNameEnum.TUITION_PRICING_COACHING,
    path: paths.tuitionPricingCoaching,
    permission: 'can_see_app_store',
    component: TuitionPricingCoachingPage,
    isNew: true,
  },
  {
    name: RouteNameEnum.ANNUAL_TUITION_INCREASES,
    path: paths.annualTuitionIncreases,
    permission: 'can_see_app_store',
    component: AnnualTuitionIncreasesPage,
    isNew: true,
  },
  {
    name: RouteNameEnum.TAXES,
    path: paths.taxes,
    permission: 'can_see_app_store',
    component: TaxesPage,
    isNew: true,
  },
  {
    name: RouteNameEnum.TAX_FORMS,
    path: paths.taxForms,
    permission: 'can_list_billing',
    component: TaxForms1099Page,
    featureFlag: FeatureFlagEnum.FINANCES,
    isNew: true,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.TAX_FORMS) || shouldHideTaxForms(),
  },
  {
    name: RouteNameEnum.SUBSIDY_PREPAYMENT,
    path: paths.subsidyPrepayment,
    permission: 'can_see_app_store',
    component: SubsidyPrepaymentPage,
    isNew: true,
  },
  {
    name: RouteNameEnum.SUBSIDY_PREPAYMENT_NOVA,
    path: paths.external.subsidyPrepaymentNova,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.SUBSIDY_PREPAYMENT_NOVA),
  },
  {
    name: RouteNameEnum.LAUNCH_NOVA,
    path: paths.external.launchNova,
    shouldOpenInNewTab: true,
    permission: 'can_list_marketplace_links',
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.LAUNCH_NOVA),
  },
  {
    name: RouteNameEnum.SETUP_ASSISTANT,
    path: paths.external.setupAssistant,
    permission: 'can_list_marketplace_links',
    shouldOpenInNewTab: true,
    featureFlag: FeatureFlagEnum.MARKETPLACE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.SETUP_ASSISTANT),
  },
  {
    name: RouteNameEnum.TRI_SHARE,
    path: paths.triShare,
    permission: 'can_see_app_store',
    component: TriSharePage,
    isNew: true,
  },
  {
    name: RouteNameEnum.DEBIT_CARD,
    path: paths.debitCard,
    permission: 'can_see_app_store',
    component: DebitCardPage,
    isNew: true,
  },
  {
    name: RouteNameEnum.EARN_INTEREST,
    path: paths.earnInterest,
    permission: 'can_see_app_store',
    component: EarnInterestPage,
    isNew: true,
  },
  {
    name: RouteNameEnum.INSURANCE,
    path: paths.insurance,
    permission: 'can_see_app_store',
    component: InsurancePage,
    isNew: true,
  },
  {
    name: RouteNameEnum.PAYROLL,
    path: paths.payroll,
    permission: 'can_see_app_store',
    component: PayrollPage,
    isNew: true,
  },
  {
    name: RouteNameEnum.WHITE_LABEL_WEBSITE,
    path: paths.whiteLabelWebsite,
    permission: 'can_see_app_store',
    component: WhiteLabelWebsitePage,
    isNew: true,
  },
  {
    name: RouteNameEnum.LICENSING_MANAGEMENT,
    path: 'https://nm.staging.wonderschool.io/director/license/fcc',
    permission: 'can_list_moxit_organizations',
    shouldOpenInNewTab: true,
  },
  {
    name: RouteNameEnum.NM_LICENSING,
    // Links directly to NM subdomain login to establish session on correct domain
    // After auth, user is redirected to the provider signup page
    path: `${getConfig().wsConfig.nmLicensingUrl}/login?redirect_to=/director/sign-up/form`,
    shouldOpenInNewTab: false,
  },
  {
    name: RouteNameEnum.BUSINESS_COACHING,
    path: 'https://survey.wonderschool.com/',
    permission: 'can_list_moxit_organizations',
    shouldOpenInNewTab: true,
  },
  {
    name: RouteNameEnum.FORMS_BUILDER,
    path: paths.formsBuilder,
    permission: 'can_list_moxit_organizations',
  },
  {
    name: RouteNameEnum.SYSTEM_NOTIFICATIONS,
    path: paths.systemNotifications,
    permission: 'can_list_moxit_organizations',
    component: Admin,
  },
  // APPS
  {
    name: RouteNameEnum.APPS,
    path: paths.appstore,
    component: AppStore,
    featureFlag: FeatureFlagEnum.APP_STORE,
    permission: 'can_see_app_store',
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.APPS) || shouldHideAppStore(),
  },
  {
    name: RouteNameEnum.LEARNING_BEYOND_PAPER,
    path: paths.learningBeyondPaper,
    component: LearningBeyondPaper,
    featureFlag: FeatureFlagEnum.APP_STORE,
    shouldHide: () => shouldHideForNetwork(RouteNameEnum.APPS) || shouldHideAppStore(),
  },
  {
    name: RouteNameEnum.KINDER_CONNECT,
    path: paths.kinderConnect,
    component: KinderConnectPage,
    shouldHide: () => shouldHideAppStore(),
  },
  {
    name: RouteNameEnum.OPEN_LEDGER,
    path: paths.openLedger,
    component: OpenLedgerPage,
    shouldHide: () => shouldHideAppStore() || shouldHideOpenLedger(),
  },
  {
    name: RouteNameEnum.WONDERCHOOL_APP,
    path: paths.wonderschoolApp,
    component: WonderschoolApp,
  },
];

export function getRoutes(): RouteType[] {
  return routes;
}
